import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { CategoryExplorer } from "@/components/CategoryExplorer";
import { getRelatedCategories } from "@/data/category-groups";
import {
  categories,
  getCategory,
  CATEGORY_PARENTS,
  CATEGORY_SLUG_REDIRECTS,
} from "@/data/symbols";

export const Route = createFileRoute("/categories/$slug")({
  loader: ({ params }) => {
    // v2 taxonomy convergence: old short slugs permanently redirect to the
    // canonical `-symbols` route so there is no duplicate indexable URL.
    const moved = CATEGORY_SLUG_REDIRECTS[params.slug];
    if (moved) throw redirect({ to: "/categories/$slug", params: { slug: moved }, statusCode: 301 });
    const cat = getCategory(params.slug);
    if (!cat) throw notFound();
    return cat;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] };
    const title = `${loaderData.name} — copy and paste Unicode symbols`;
    const description = `${loaderData.description} ${loaderData.symbols.length} symbols available, one click to copy.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: abs(`/categories/${loaderData.slug}`) },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: abs(`/categories/${loaderData.slug}`) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: loaderData.name,
            description: loaderData.description,
            url: `/categories/${loaderData.slug}`,
            numberOfItems: loaderData.symbols.length,
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1 items-center justify-center p-12 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold">Category not found</h1>
          <Link to="/categories" className="mt-4 inline-block text-accent hover:underline">
            ← Browse all categories
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  ),
  component: CategoryPage,
});

function CategoryPage() {
  const cat = Route.useLoaderData();
  const related = getRelatedCategories(cat.slug);

  // Parent/child relationships (taxonomy contract): a child links up to its
  // parent; a parent links down to its children.
  const parentSlug = CATEGORY_PARENTS[cat.slug];
  const parent = parentSlug ? getCategory(parentSlug) : undefined;
  const children = categories.filter((c) => CATEGORY_PARENTS[c.slug] === cat.slug);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 pt-8 sm:px-6 sm:pt-16">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/categories" className="hover:text-foreground">Categories</Link>
            <span>/</span>
            <span className="text-foreground">{cat.name}</span>
          </nav>

          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-start sm:gap-5">
            <div className="text-5xl text-gradient sm:text-7xl">{cat.emoji}</div>
            <div className="min-w-0">
              <h1 className="break-words font-display text-3xl font-bold sm:text-5xl">{cat.name}</h1>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">{cat.description}</p>
            </div>
          </div>
        </section>

        {parent || children.length > 0 ? (
          <section className="mx-auto w-full max-w-7xl px-4 pb-2 sm:px-6">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {parent ? (
                <Link
                  to="/categories/$slug"
                  params={{ slug: parent.slug }}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-surface/60 px-3 py-1 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  ↑ {parent.name}
                </Link>
              ) : null}
              {children.map((c) => (
                <Link
                  key={c.slug}
                  to="/categories/$slug"
                  params={{ slug: c.slug }}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-surface/60 px-3 py-1 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        <section className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6">
          <CategoryExplorer
            symbols={cat.symbols}
            categoryName={cat.name}
          />
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
          <h2 className="mb-4 font-display text-xl font-semibold">{related.heading}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {related.items.map((c) => (
              <Link
                key={c.slug}
                to="/categories/$slug"
                params={{ slug: c.slug }}
                className="glass flex items-center gap-3 rounded-xl p-4 transition-colors hover:border-primary/40"
              >
                <span className="text-2xl text-gradient">{c.emoji}</span>
                <span className="flex-1">
                  <span className="block font-semibold">{c.name}</span>
                  <span className="block text-xs text-muted-foreground">{c.tagline}</span>
                </span>
                <span className="text-muted-foreground">→</span>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
